import { useCallback, useEffect, useMemo, useState } from "react";
import { useApolloClient, useSubscription } from "@apollo/client";
import { SessionContext } from "../context/SessionContext";
import { GET_SESSION } from "../../graphql/query/Session";
import { USER_PERMISSIONS_SUB } from "../../graphql/subscription/UserPermissions";
import {
  clearSession,
  isTokenUsable,
  onSessionInvalid,
  pickHeadquarterId,
  setLogoutNotice,
  setSelectedHeadquarterId,
  setToken,
} from "../../utils/session";

// Owns the signed-in session for the whole app.
//
// The browser no longer knows who it is by decrypting its token — it cannot.
// It asks the API (`meResolver`) on load and after login, and again whenever
// the API signals that the session changed. Everything else reads the result
// through useSession().

const ANONYMOUS = {
  status: "anonymous",
  kind: null,
  user: null,
  student: null,
};

const isAuthError = (error) =>
  error?.graphQLErrors?.some((e) => e.extensions?.code === "UNAUTHENTICATED");

export const SessionProvider = ({ children }) => {
  const client = useApolloClient();

  // A stored token that is still in date means "probably signed in" — show a
  // loader, not the login page, until the API confirms it. One that is not —
  // expired, or an AES token from before the JWT change — is thrown away now:
  // left in place it would ride along on every request, and a public page
  // (the enrolment form) would treat the API rejecting it as "your session
  // ended" and bounce a signed-out visitor to the login page.
  const [session, setSession] = useState(() => {
    if (isTokenUsable()) return { ...ANONYMOUS, status: "loading" };

    clearSession();
    return ANONYMOUS;
  });
  const [selectedHeadquarterId, setHeadquarterState] = useState(null);

  const logout = useCallback((notice) => {
    clearSession();
    setLogoutNotice(notice);

    // A full page load rather than a route change: it drops the Apollo cache,
    // every piece of GlobalContext state and the websocket together with the
    // token, so nothing of this user survives into whoever signs in next.
    window.location.assign("/login");
  }, []);

  const loadSession = useCallback(async () => {
    const { data } = await client.query({
      query: GET_SESSION,
      fetchPolicy: "network-only",
      // Sent without the sede header: if the stored sede is no longer one of
      // this user's, the API refuses every request that carries it — this one
      // included — and the session could never load to fix it.
      context: { withoutHeadquarter: true },
    });

    const me = data?.meResolver;
    if (!me) throw new Error("La sesión no devolvió datos.");

    let headquarterId = null;
    if (me.kind === "USER") {
      headquarterId = pickHeadquarterId(me.user?.headquarters || []);
      setSelectedHeadquarterId(headquarterId);
    }

    setHeadquarterState(headquarterId);
    setSession({
      status: "authenticated",
      kind: me.kind,
      user: me.user,
      student: me.student,
    });

    return me;
  }, [client]);

  // First load: confirm a stored token with the API.
  useEffect(() => {
    if (session.status !== "loading") return;

    loadSession().catch((error) => {
      // An auth failure is already being handled: the error link raised
      // onSessionInvalid, which logs out. Anything else — the API is down —
      // is not a reason to throw the session away.
      if (!isAuthError(error)) {
        setSession({ ...ANONYMOUS, status: "error" });
      }
    });
    // Runs once, on mount.
  }, []);

  // The API said the session is over: expired, revoked, account disabled.
  useEffect(
    () => onSessionInvalid((_reason, message) => logout(message)),
    [logout],
  );

  /**
   * Stores a token from a login mutation and loads its session. Resolves to
   * the session, so the caller can route by kind.
   */
  const login = useCallback(
    async (token) => {
      setToken(token);
      setSession({ ...ANONYMOUS, status: "loading" });

      try {
        return await loadSession();
      } catch (error) {
        clearSession();
        setSession(ANONYMOUS);
        throw error;
      }
    },
    [loadSession],
  );

  const refresh = useCallback(
    () => loadSession().catch(() => null),
    [loadSession],
  );

  const selectHeadquarter = useCallback(
    async (headquarterId) => {
      setSelectedHeadquarterId(headquarterId);
      setHeadquarterState(headquarterId);

      // Every request reads the sede when it is sent, so refetching is all it
      // takes for what is on screen to follow the switch.
      await client.refetchQueries({ include: "active" });
    },
    [client],
  );

  // The API tells a staff user's open tab when their account, their permission
  // profile, or their password changed. It sends only a signal — the data is
  // refetched through meResolver.
  useSubscription(USER_PERMISSIONS_SUB, {
    skip: !(session.status === "authenticated" && session.kind === "USER"),
    onData: ({ data }) => {
      const event = data?.data?.userPermissionsSubs;
      if (!event) return;

      if (event.logout) {
        logout("Tu contraseña cambió. Volvé a iniciar sesión.");
      } else {
        refresh();
      }
    },
  });

  const value = useMemo(
    () => ({
      ...session,
      selectedHeadquarterId,
      login,
      logout,
      refresh,
      selectHeadquarter,
    }),
    [session, selectedHeadquarterId, login, logout, refresh, selectHeadquarter],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};
